import React, { useCallback, useEffect, useState } from "react";
import {
    Box,
    CircularProgress,
    Stack,
    Typography,
    useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Dayjs } from "dayjs";
import Booking, { BookingState } from "../../../api/Booking";
import Service from "../../../api/Service";
import ConfirmButton from "../../ConfirmButton";
import TextModalButton from "../../TextModalButton";
import DateTimePicker from "../../Modals/DateTimePicker";
import { useCookies } from "react-cookie";
import { parseWithOffset } from "../../../utils/dayjsUtils";
import { FullScreenLoader } from "../../FullScreenLoader";
import FormattedPrice from "../../FormattedPrice";
import FormattedTime from "../../FormattedTime";
import EmptyListState from "../../EmptyListState";
import EventBusyRounded from "@mui/icons-material/EventBusyRounded";
import { FONTS, CARD_STYLES } from "../../../constants/styles";

interface BookingsProps {
    user_id: string;
}

interface BookingSectionProps {
    title: string;
    bookings: Booking[];
    type: "Waitlist" | "Active" | "History";
    reloadBookings: () => Promise<void>;
    startIndex: number;
}

interface BookingItemProps {
    booking: Booking;
    reloadBookings: () => Promise<void>;
    type: "Waitlist" | "Active" | "History";
    index: number;
}

const Bookings = (props: BookingsProps) => {
    const { user_id } = props;

    const [cookies] = useCookies(["uuid", "brand_id", "location_id"]);
    const { uuid, brand_id, location_id } = cookies;

    const [waitlist, setWaitlist] = useState<Booking[]>([]);
    const [activeBookings, setActiveBookings] = useState<Booking[]>([]);
    const [historyBookings, setHistoryBookings] = useState<Booking[]>([]);
    const [loading, setLoading] = useState(true);

    const reloadAllBookings = useCallback(async () => {
        setLoading(true);
        try {
            const waitlist = await Booking.listUser(
                uuid,
                brand_id,
                location_id,
                "waiting",
                user_id
            );
            const active = await Booking.listUser(
                uuid,
                brand_id,
                location_id,
                "active",
                user_id
            );
            const history = await Booking.listUser(
                uuid,
                brand_id,
                location_id,
                "history",
                user_id
            );
            setWaitlist(waitlist || []);
            setActiveBookings(active || []);
            setHistoryBookings(history || []);
        } finally {
            setLoading(false);
        }
    }, [uuid, brand_id, location_id, user_id]);

    useEffect(() => {
        reloadAllBookings();
    }, [reloadAllBookings]);

    if (loading) return <FullScreenLoader />;

    const isEmpty =
        waitlist.length === 0 &&
        activeBookings.length === 0 &&
        historyBookings.length === 0;

    if (isEmpty) {
        return (
            <EmptyListState
                primaryText="No bookings yet"
                icon={
                    <EventBusyRounded
                        sx={{
                            fontSize: 28,
                            color: "text.secondary",
                            opacity: 0.5,
                        }}
                    />
                }
            />
        );
    }

    let staggerIndex = 0;

    return (
        <Stack spacing={3} sx={{ px: 2, pt: 0.5, pb: 3 }}>
            {waitlist.length > 0 && (
                <BookingSection
                    title="Waitlist"
                    bookings={waitlist}
                    type="Waitlist"
                    reloadBookings={reloadAllBookings}
                    startIndex={(() => {
                        const idx = staggerIndex;
                        staggerIndex += waitlist.length;
                        return idx;
                    })()}
                />
            )}
            {activeBookings.length > 0 && (
                <BookingSection
                    title="Active bookings"
                    bookings={activeBookings}
                    type="Active"
                    reloadBookings={reloadAllBookings}
                    startIndex={(() => {
                        const idx = staggerIndex;
                        staggerIndex += activeBookings.length;
                        return idx;
                    })()}
                />
            )}
            {historyBookings.length > 0 && (
                <BookingSection
                    title="Client history"
                    bookings={historyBookings}
                    type="History"
                    reloadBookings={reloadAllBookings}
                    startIndex={(() => {
                        const idx = staggerIndex;
                        staggerIndex += historyBookings.length;
                        return idx;
                    })()}
                />
            )}
        </Stack>
    );
};

const BookingSection = (props: BookingSectionProps) => {
    const theme = useTheme();
    const primary = theme.palette.primary.main;
    const { title, bookings, type, reloadBookings, startIndex } = props;

    return (
        <Stack spacing={1.5}>
            {/* Section header — matches alphabet group header pattern */}
            <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ px: 0.5 }}
            >
                <Typography
                    fontFamily={FONTS.display}
                    fontWeight={700}
                    fontSize="14px"
                    color={primary}
                    sx={{ flexShrink: 0 }}
                >
                    {title}
                </Typography>
                <Box flex={1} height="1px" bgcolor={alpha(primary, 0.1)} />
            </Stack>

            {/* Booking cards */}
            {bookings.map((booking, i) => (
                <BookingItem
                    key={booking.key ?? i}
                    booking={booking}
                    type={type}
                    reloadBookings={reloadBookings}
                    index={startIndex + i}
                />
            ))}
        </Stack>
    );
};

const BookingItem = (props: BookingItemProps) => {
    const [service, setService] = useState<Service | undefined>();

    const theme = useTheme();
    const primary = theme.palette.primary.main;
    const { booking, type, reloadBookings, index } = props;
    const { uuid, brand_id, location_id } = useCookies([
        "uuid",
        "brand_id",
        "location_id",
    ])[0];

    useEffect(() => {
        if (!uuid || !brand_id) return;
        Service.info(uuid, brand_id, booking.service)
            .then(setService)
            .catch(console.error);
    }, [uuid, brand_id, booking.service]);

    const cancelBooking = useCallback(
        async (close: () => void) => {
            if (!uuid || !brand_id || !location_id) return;
            await Booking.updateState(
                uuid,
                brand_id,
                location_id,
                booking.key,
                BookingState.Cancel
            );
            reloadBookings();
            close();
        },
        [uuid, brand_id, location_id, booking, reloadBookings]
    );

    const saveNote = useCallback(
        async (text: string, close: () => void) => {
            if (!uuid || !brand_id || !location_id) return;
            await Booking.updateNote(
                uuid,
                brand_id,
                location_id,
                booking.key,
                text
            );
            reloadBookings();
            close();
        },
        [uuid, brand_id, location_id, booking, reloadBookings]
    );

    const handleScheduling = useCallback(
        async (dateTime: Dayjs, close: () => void, action: string) => {
            const endDate = dateTime
                .add(service?.duration || 0, "minute")
                .toDate();
            if (action === "scheduleAgain") {
                await Booking.register(
                    uuid,
                    brand_id,
                    location_id,
                    booking.service,
                    booking.user,
                    service!.duration,
                    service!.price,
                    dateTime.toDate(),
                    endDate,
                    booking.confirmed
                );
            } else {
                await Booking.update(
                    uuid,
                    brand_id,
                    location_id,
                    booking.key,
                    dateTime.toDate(),
                    endDate
                );
            }
            reloadBookings();
            close();
        },
        [uuid, brand_id, location_id, booking, service, reloadBookings]
    );

    if (!service) return <CircularProgress size={24} sx={{ mx: "auto" }} />;

    const isActive = type === "Active";
    const isCancelled = booking.state === "cancel";

    const accentColor = isCancelled
        ? theme.palette.error.main
        : type === "Waitlist"
        ? "#FF9800"
        : isActive
        ? primary
        : "transparent";

    return (
        <Stack
            sx={{
                ...CARD_STYLES,
                p: 2,
                borderLeft: `4px solid ${accentColor}`,
                "@keyframes fadeSlideUp": {
                    from: { opacity: 0, transform: "translateY(10px)" },
                    to: { opacity: 1, transform: "translateY(0)" },
                },
                animation: `fadeSlideUp 0.25s ease-out ${index * 30}ms both`,
            }}
            spacing={1}
        >
            {/* Row 1: date + status pill */}
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
            >
                <Typography fontSize="13px" color="text.secondary">
                    {type === "Waitlist"
                        ? "As soon as possible"
                        : parseWithOffset(booking.start).format(
                              isActive ? "MMMM D, HH:mm" : "DD.MM.YYYY"
                          )}
                </Typography>
                {isCancelled && (
                    <Typography
                        fontSize="12px"
                        fontWeight={500}
                        color="error.main"
                        sx={{
                            bgcolor: alpha(
                                theme.palette.error.main,
                                0.08
                            ),
                            borderRadius: "8px",
                            px: 0.75,
                            py: 0.25,
                        }}
                    >
                        Cancelled
                    </Typography>
                )}
            </Stack>

            {/* Row 2: service name — full width */}
            <Typography
                fontFamily={FONTS.display}
                fontSize="16px"
                fontWeight={600}
                lineHeight={1.3}
                sx={{ overflowWrap: "break-word", wordBreak: "break-word" }}
            >
                {service.name}
            </Typography>

            {/* Row 3: price + duration */}
            <Stack direction="row" alignItems="center" spacing={1.5}>
                <FormattedPrice
                    price={service.price}
                    currency={service.currency}
                    discount={booking.discount}
                />
                <FormattedTime time={service.duration} size="small" />
            </Stack>

            {/* Divider + Row 4: action buttons horizontal */}
            {!isCancelled && (
                <>
                    <Box sx={{ height: "1px", bgcolor: alpha(primary, 0.08), mx: -0.5 }} />
                    <Stack
                        direction="row"
                        flexWrap="wrap"
                        gap={0.75}
                    >
                    {type === "Waitlist" ? (
                        <>
                            <DateTimePicker
                                duration={service.duration}
                                button={{
                                    variant: "small",
                                    color: "transparent",
                                }}
                                onSave={(dateTime, close) =>
                                    handleScheduling(
                                        dateTime,
                                        close,
                                        "schedule"
                                    )
                                }
                            >
                                Schedule
                            </DateTimePicker>
                            <ConfirmButton
                                button={{
                                    variant: "small",
                                    color: "transparent",
                                }}
                                onConfirm={cancelBooking}
                            >
                                Delete from Waitlist
                            </ConfirmButton>
                        </>
                    ) : type === "Active" ? (
                        <>
                            <DateTimePicker
                                duration={service.duration}
                                button={{
                                    variant: "small",
                                    color: "transparent",
                                }}
                                onSave={(dateTime, close) =>
                                    handleScheduling(
                                        dateTime,
                                        close,
                                        "reschedule"
                                    )
                                }
                            >
                                Reschedule
                            </DateTimePicker>
                            <ConfirmButton
                                button={{
                                    variant: "small",
                                    color: "transparent",
                                }}
                                onConfirm={cancelBooking}
                            >
                                Cancel reservation
                            </ConfirmButton>
                        </>
                    ) : (
                        <>
                            <TextModalButton
                                placeholder="Note..."
                                button={{
                                    variant: "small",
                                    color: "transparent",
                                }}
                                onSave={saveNote}
                                note={booking.note}
                            >
                                Add/Edit Note
                            </TextModalButton>
                            <DateTimePicker
                                duration={service.duration}
                                button={{
                                    variant: "small",
                                    color: "transparent",
                                }}
                                onSave={(dateTime, close) =>
                                    handleScheduling(
                                        dateTime,
                                        close,
                                        "scheduleAgain"
                                    )
                                }
                            >
                                Schedule Again
                            </DateTimePicker>
                        </>
                    )}
                    </Stack>
                </>
            )}

            {/* Note preview if exists */}
            {booking.note && (
                <Typography
                    fontSize="12px"
                    color="text.secondary"
                    fontStyle="italic"
                    sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                    }}
                >
                    &ldquo;{booking.note}&rdquo;
                </Typography>
            )}
        </Stack>
    );
};

export default Bookings;
